import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE = 'https://idliapam.in';
const DEFAULT_IMAGE = `${SITE}/og-image.png`;
const TWITTER_HANDLE = '@idliapam';

interface SEOProps {
  title: string;
  description: string;
  path?: string;
  image?: string;
  keywords?: string;
  type?: 'website' | 'product' | 'article';
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  path = '',
  image = DEFAULT_IMAGE,
  keywords = '',
  type = 'website',
  jsonLd,
}) => {
  const url = `${SITE}${path}`;
  const fullTitle = title.includes('Idliapam') ? title : `${title} | Idliapam`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Idliapam" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(jsonLd) ? jsonLd : jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

// Reusable JSON-LD schemas

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Idliapam',
  url: SITE,
  logo: `${SITE}/logo192.png`,
  description:
    'AI-powered SaaS platform — bug reporting (Glitch), in-app announcements (Radar), WhatsApp commerce API (Chutney), and D2C dashboard (AI Commerce OS).',
  foundingDate: '2025',
  founder: {
    '@type': 'Person',
    name: 'Ankita Jain',
  },
  sameAs: [
    'https://www.linkedin.com/company/idliapam',
    'https://twitter.com/idliapam',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'ankitajainkuniya@gmail.com',
    contactType: 'sales',
  },
};

export const softwareSchema = (product: {
  name: string;
  description: string;
  url: string;
  image?: string;
  price?: string;
  priceCurrency?: string;
  category?: string;
  features?: string[];
}) => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: product.name,
  description: product.description,
  url: product.url,
  image: product.image || DEFAULT_IMAGE,
  applicationCategory: product.category || 'BusinessApplication',
  operatingSystem: 'Web',
  offers: product.price
    ? {
        '@type': 'Offer',
        price: product.price,
        priceCurrency: product.priceCurrency || 'INR',
        priceValidUntil: '2027-12-31',
        availability: 'https://schema.org/InStock',
      }
    : {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'INR',
        description: 'Free trial available',
      },
  ...(product.features && {
    featureList: product.features.join(', '),
  }),
});

export const faqSchema = (faqs: { q: string; a: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.a,
    },
  })),
});

export const breadcrumbSchema = (items: { name: string; url: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: item.url,
  })),
});

export default SEO;
